import React from 'react';
import { Lead, PipelineStage } from '@/types';
import { LeadCard } from './LeadCard';
import { cn } from '@/utils';

interface PipelineColumnProps {
  stage: PipelineStage;
  leads: Lead[];
  onStageChange?: (leadId: string, newStage: string) => void;
}

export const PipelineColumn: React.FC<PipelineColumnProps> = ({ 
  stage, 
  leads, 
  onStageChange 
}) => {
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'New':
        return 'bg-blue-500/10 border-blue-500/30';
      case 'Engaged':
        return 'bg-emerald-500/10 border-emerald-500/30';
      case 'Stalled':
        return 'bg-amber-500/10 border-amber-500/30';
      case 'Dropped':
        return 'bg-red-500/10 border-red-500/30';
      case 'Converted':
        return 'bg-teal-500/10 border-teal-500/30';
      default:
        return 'bg-slate-500/10 border-slate-500/30';
    }
  };

  const getTodayChange = () => {
    // Mock data for daily changes
    const changes: Record<string, number> = {
      'New': 23,
      'Engaged': 12,
      'Stalled': 8,
      'Dropped': 3,
      'Converted': 15,
    };
    return changes[stage] || 0;
  };

  const todayChange = getTodayChange();

  return (
    <div className={cn(
      'flex flex-col border-2 rounded-lg min-h-96',
      getStageColor(stage)
    )}>
      {/* Column Header */}
      <div className="p-4 border-b border-slate-600">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-50">
            {stage}
          </h3>
          <div className="text-sm text-slate-400">
            {leads.length} leads
          </div>
        </div>
        <div className="text-xs text-slate-400">
          +{todayChange} today
        </div>
      </div>

      {/* Column Content */}
      <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-96">
        {leads.length > 0 ? (
          leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onStageChange={onStageChange}
            />
          ))
        ) : (
          <div className="text-center text-slate-500 py-8">
            No leads in this stage
          </div>
        )}
      </div>

      {/* Drop Zone Indicator (for drag & drop) */}
      <div className="p-2 text-center text-xs text-slate-500 border-t border-slate-600/50">
        Drop leads here
      </div>
    </div>
  );
};